import { useModalStatusMessage } from '@/hooks';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import { useMutation } from '@apollo/client';
import GlobalModal from '@components/GlobalModal';
import ModalActions from '@components/modal/ModalActions';
import ModalContent from '@components/modal/ModalContent';
import ModalTitle from '@components/modal/ModalTitle';
import { zodResolver } from '@hookform/resolvers/zod';
import { I18n } from '@lingui/core';
import { msg, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { TextField } from '@mui/material';
import { type FC } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import CREATE_DEVICE_OBSERVATION from '../../graphql/mutations/createDeviceObservation';

interface Props {
  deviceId: string;
  initialContent?: string;
  onClose: () => void;
}

const schema = (i18n: I18n) =>
  z.object({
    content: z.string().min(2, t(i18n)`El contenido debe tener al menos 2 caracteres`),
  });

type FormValues = z.infer<ReturnType<typeof schema>>;

const Component: FC<Props> = ({ deviceId, initialContent, onClose }) => {
  const { _, i18n } = useLingui();
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();
  const [createObservation] = useMutation(CREATE_DEVICE_OBSERVATION, {
    onCompleted() {
      openModalSuccessMessage(_(msg`Observación creada correctamente`));
      onClose();
    },
    onError(error) {
      openModalErrorMessage(error.message);
    },
    update(cache) {
      cache.modify({
        id: cache.identify({
          __typename: 'DeviceProfile',
          id: deviceId,
        }),
        fields: {
          observations(_, { DELETE }) {
            return DELETE;
          },
        },
      });
    },
  });

  const methods = useForm<FormValues>({
    defaultValues: {
      content: initialContent,
    },
    resolver: zodResolver(schema(i18n)),
  });

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    createObservation({
      variables: {
        input: {
          deviceId,
          content: data.content,
        },
      },
    });
  };

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)}>
      <ModalTitle onClose={onClose} title={_(msg`Crear observación`)} />
      <ModalContent dividers>
        <Controller
          name="content"
          control={methods.control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={_(msg`Contenido`)}
              fullWidth
              rows={4}
              multiline
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </ModalContent>
      <ModalActions onClose={onClose} onSubmit="submit" />
    </form>
  );
};

export const CreateDeviceObservationModal: FC = () => {
  const { globalModalProps, state, onClose } = useGlobalModal('createDeviceObservationModal');

  return (
    <GlobalModal {...globalModalProps} dialogProps={{ maxWidth: 'sm' }}>
      {state.open && (
        <Component
          deviceId={state.deviceId}
          initialContent={state.initialContent}
          onClose={onClose}
        />
      )}
    </GlobalModal>
  );
};
